import { createApi } from '@reduxjs/toolkit/query/react';

import { API_BASE_URL } from '@/config';
import { nonAbortingSignalBaseQuery } from '@/store/services/baseQuery';

const trimIncompleteLatestPoint = (points) => (points.length > 0 ? points.slice(0, -1) : []);

const audiencesApi = createApi({
    baseQuery: nonAbortingSignalBaseQuery,
    endpoints: (builder) => ({
        getAudienceCounts: builder.query({
            queryFn: async ({ audienceIds, country }, _api, _extraOptions, baseQuery) => {
                const results = await Promise.all(
                    audienceIds.map((id) =>
                        baseQuery(
                            `${API_BASE_URL}/audiences/${encodeURIComponent(id)}/counts/${encodeURIComponent(country)}`,
                        ),
                    ),
                );
                const firstError = results.find((r) => r.error);
                if (firstError) return { error: firstError.error };
                return {
                    data: Object.fromEntries(
                        audienceIds.map((id, index) => [id, results[index].data?.counts?.monthly ?? null]),
                    ),
                };
            },
        }),
        getAudiences: builder.query({
            queryFn: async (_arg, _api, _extraOptions, baseQuery) => {
                const result = await baseQuery(`${API_BASE_URL}/audiences`);
                if (result.error) {
                    return { error: { error: 'Unable to fetch audience details.', status: 'CUSTOM_ERROR' } };
                }
                if (!Array.isArray(result.data?.audiences)) {
                    return { error: { error: 'Unable to fetch audience details.', status: 'CUSTOM_ERROR' } };
                }
                return {
                    data: Object.fromEntries(result.data.audiences.map((audience) => [audience.id, audience])),
                };
            },
        }),
        getBatchSizesByCountry: builder.query({
            queryFn: async ({ audienceIds, country }, _api, _extraOptions, baseQuery) => {
                const results = await Promise.all(
                    audienceIds.map((id) =>
                        baseQuery(
                            `${API_BASE_URL}/audiences/${encodeURIComponent(id)}/batch-sizes-by-country-graph/${encodeURIComponent(country)}`,
                        ),
                    ),
                );
                const firstError = results.find((r) => r.error);
                if (firstError) return { error: firstError.error };
                return {
                    data: Object.fromEntries(
                        audienceIds.map((id, index) => [
                            id,
                            trimIncompleteLatestPoint(results[index].data?.batch_sizes_by_country_graph ?? []),
                        ]),
                    ),
                };
            },
        }),
    }),
    reducerPath: 'audiencesApi',
});

export const { useGetAudienceCountsQuery, useGetAudiencesQuery, useGetBatchSizesByCountryQuery } = audiencesApi;
export default audiencesApi;
